/* @flow */

import { SearchResultDebugInfo, SearchResultLtrDebugInfo } from "./SearchResult";
import { TrackingTags } from "../../tracking/Tracking";
import {
    PromotionLabel,
    SellingPriceDnrInfo,
    PriceHiddenReason,
    PriceAndPromotionInfo
} from "../PriceAndPromotionInfo";
import { Category } from "../../types/EvaluateArticleNew";

export type Availability = string;

export type BundleId = {
    readonly bettyBundleId: string;
};
export type BettyBundleId = {
    readonly bundleNumber: string;
    readonly bettyBundleId: string;
    readonly bettyVariantId: string | undefined | null;
    readonly variantNumber: string | undefined | null;
    readonly articleNumber: string | undefined | null;
};
export type VariantId = {
    readonly bettyVariantId: string;
    readonly bettyVariantNumber: string;
};

export type Offer = {
    active: boolean;
    availability: string;
    bettyOfferId: string;
    channels: any;
    description: string | undefined | null;
    deliveryModes: {
        [x: string]: {
            price: {
                currency: string;
                unitGross: number;
                unitNet: number;
                strikeThroughNet?: number;
                strikeThroughGross?: number;
            };
            leadtime: {
                amount: number;
                unit: string;
                deliveryDate: string;
            };
        };
    };
    offerId: string;
    rank: number;
    shopId: string;
    shopName: string;
    stock: number;
};

export type WeightPerPieceUom = {
    uom: string;
    value: number;
};

export type WeightPerPieceUnit = {
    unit: string | undefined;
    value: number | undefined;
};

// Should replace with proper BettyBundle type at some point.
export type Bundle = {
    bundleId: BundleId;
    bettyBundleId: BettyBundleId;
    categories: Category[];
    customerDisplayId: string;
    // This `content` is not part of the BettyBundle API.
    // Maybe this is supposed to be contentData of type BettyBundleContent.
    content: ContentData | undefined | null;
    description: string;
    brandName: string | undefined | null;
    imageUrl: string;
    fallbackImageUrl: string;
    availability: Availability;
    customerAvailability: Availability;
    employeeAvailability: Availability;
    showCustomerAvailability: boolean;
    showEmployeeAvailability: boolean;
    selectedOffer?: string;
    isWeightArticle: "WEIGHT" | "NON_WEIGHT";
    weightPerPiece: WeightPerPieceUom | undefined | null;
    offers: Offer[];
    trackingInfo: {
        trackingId: string;
    };
    stores: {
        [storeId: string]: BundleStoreData;
    };
    minOrderQuantity?: number;
    ownBrand: string;
    // Seems like `recentlyAddedExpiration` is not used at all.
    // Also, this is not part of the BettyBundle API.
    recentlyAddedExpiration: Date | undefined | null;
    refBundleIds: ReferencedBundleIds[] | undefined | null;
};

export type BuyabilityRenderData = {
    customer: boolean;
    employee: boolean;
    deliveryMode: string | null;
};

export type AvailabilityRenderData = {
    deliveryMode: string | undefined | null;
    deliveryDate: Date | undefined | null;
    availability: Availability | undefined | null;
    showAvailability: boolean;
    delisted: boolean;
};

export type ArticleIdsRenderData = {
    articleVariantId: string;
    idForBasket: string | undefined | null;
    selectedBundleId: string;
    forListActions: {
        newBundleId: BettyBundleId | string | undefined | null;
        bettyVariantId: string;
        idForList: string;
        originalBundleId: string | undefined | null;
    };
    idForDisplay: string;
    trackingId: string;
    offerId: string | undefined | null;
    childBundleIds: string[] | null | undefined;
};

export type AddToBasketProps = {
    isDeprecated: boolean;
    isNonExistentBundle: boolean;
    isCartEnabled: boolean;
    customerBuyable: boolean;
    employeeBuyable: boolean;
};

export type Location = {
    pathname: string | undefined;
    query:
        | {
              [x: string]: string;
          }
        | undefined;
};

export type ImageUrls = {
    bundle?: string | null;
    bundleFallBack?: string | null;
    variant?: string | null;
};

export type ListInformation = {
    listId: string | undefined | null;
    listType: string | undefined | null;
    listName: string | undefined | null;
    listAmount: number | undefined | null;
};

export type ArticleBannerCardRenderData = {
    priceAndPromotionInfo: PriceAndPromotionInfo;
    isWeightArticle: boolean;
    weightPerPiece: WeightPerPieceUnit | undefined | null;
    pvLink: Location;
    marketPlaceShopInfo: {
        shopId: string | undefined | null;
        shopName: string;
    };
    marketplace: boolean;
    description: string;
    imageUrls: ImageUrls;
    minOrderQuantity?: number;
    recentlyAddedExpiration: Date | undefined | null;
};

export type ArticleCardBundleRenderData = {
    buyability: BuyabilityRenderData;
    availabilityInfo: AvailabilityRenderData;
    priceAndPromotionInfo: PriceAndPromotionInfo;
    pvLink: Location;
    marketPlaceShopInfo: {
        shopId: string | undefined | null;
        shopName: string;
    };
    marketplace: boolean;
    articleIds: ArticleIdsRenderData;
    description: string;
    imageUrls: ImageUrls;
    isWeightArticle: boolean;
    weightPerPiece: WeightPerPieceUnit | undefined | null;
    netPieceWeight: number | undefined | null;
    numberOfBundles: number;
    bundleSelector: {
        [x: string]: string;
    };
    debugInfo: SearchResultDebugInfo | undefined | null;
    ltrDebugInfo: SearchResultLtrDebugInfo | undefined | null;
    readOnly: boolean;
    listInformation: ListInformation;
    trackingInfo: TrackingTags;
    minOrderQuantity?: number;
    recentlyAddedExpiration: Date | undefined | null;
};

export type BundleStoreData = {
    customerBuyable: boolean;
    customerSearchable?: boolean;
    anonymousSearchable?: boolean;
    employeeBuyable: boolean;
    possibleDeliveryModes: {
        [x: string]: {
            [x: string]: any;
        };
    };
    sellingPriceInfo: SellingPriceInfo;
    delisted: boolean;
    selectedDeliveryMode: string | undefined | null;
    selectedFulfillmentType: string | undefined | null;
    selectedFTEvaluatePromotions: Array<EvaluatePromotion> | undefined | null;
};

export type SellingPriceFinalInfo = {
    sumNet?: number;
    sumVatAmount?: number;
    sumGross?: number;
    articleNet?: number;
    articleWithTaxesNet?: number;
    articleVatAmount?: number;
    articleGross?: number;
    articleWithTaxesGross?: number;
    emptiesNet?: number;
    emptiesVatAmount?: number;
    emptiesGross?: number;
    singleItem?: number;
    singleItemGross?: number;
    singleItemWithoutEmpties?: number;
};

export const PriceHiddenReasons = {
    ANON_CUSTOMER_VIEWING_FOOD_ARTICLE: "ANON_CUSTOMER_VIEWING_FOOD_ARTICLE" as PriceHiddenReason,
    NONFOOD_CUSTOMER_VIEWING_FOOD_ARTICLE: "NONFOOD_CUSTOMER_VIEWING_FOOD_ARTICLE" as PriceHiddenReason,
    STATUS_20_NOSTOCK_ARTICLE: "STATUS_20_NOSTOCK_ARTICLE" as PriceHiddenReason
};

export type SellingPriceInfo = {
    valid: boolean;
    currency?: string | null;
    strikeThrough?: number;
    grossStrikeThrough?: number;
    availablePromotions?: string[];
    appliedAdjustments?: string[];
    summaryDnrInfo?: SellingPriceDnrInfo;
    dnrInfo?: {
        [x: string]: SellingPriceDnrInfo;
    };
    finalPricesInfo: SellingPriceFinalInfo;
    basePrice?: number;
    deliveryPrice?: number;
    kgNet?: number;
    kgGross?: number;
    averagePerUnit?: {
        netPrice: number | undefined | null;
        grossPrice: number | undefined | null;
        vatPrice: number | undefined | null;
    };
    promotionLabels: { [k: string]: PromotionLabel };
    priceHiddenReason: PriceHiddenReason | null;
};

export type EvaluatePromotion = {
    promoId: string;
    displayPriority: number | undefined | null;
};

export type ReferencedBundleIds = {
    bundleId: string;
};

export type ContentData = {
    netPieceWeight: number | undefined | null;
};
